import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tab, { tabClasses } from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useNavigate, useSearch } from '@tanstack/react-router';

import Diagnostics from './Diagnostics';
import { StarvationBidderTab } from '../schemas';
import HowItWorks from './howItWorks/HowItWorks';
import InsightsExplorer from './insightsExplorer/InsightsExplorer';
import Planning from './planning/Planning';

import { usePlatform } from '@/features/users';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`starvation-bidder-tabpanel-${index.toString()}`}
      aria-labelledby={`starvation-bidder-tab-${index.toString()}`}
      {...other}
    >
      {value === index ? <Box pt={1.5}>{children}</Box> : null}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `starvation-bidder-tab-${index.toString()}`,
    'aria-controls': `starvation-bidder-tabpanel-${index.toString()}`,
  };
}

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 500,
  lineHeight: '1.375',
  [`&.${tabClasses.selected}`]: {
    color: theme.palette.primary.dark,
    fontWeight: 600,
  },
}));

const starvationBidderTabs: Record<StarvationBidderTab, number> = {
  diag: 0,
  iex: 1,
  how: 2,
  plan: 3,
} as const;

const StarvationBidderTabs = () => {
  const platform = usePlatform();
  const navigate = useNavigate({
    from: '/algorithm-tools/starvation-bidder',
  });
  const { starve: starvationBidderTab } = useSearch({
    from: '/_app/algorithm-tools/starvation-bidder',
  });
  const starvationTabIndex = starvationBidderTabs[starvationBidderTab];

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTabIndex: number
  ) => {
    void navigate({
      to: '/algorithm-tools/starvation-bidder',
      search: (prev) => ({
        starve: Object.keys(starvationBidderTabs)[newTabIndex],
        diag: prev.diag,
      }),
      resetScroll: false,
    });
  };

  return (
    <>
      <Box>
        <Tabs
          value={starvationTabIndex}
          onChange={handleTabChange}
          aria-label='starvation-bidder-tabs'
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: 'primary.dark',
              },
            },
          }}
        >
          <StyledTab label='Diagnostics' {...a11yProps(0)} />
          {/* TODO: enable for DV360 after client categories confirmation */}
          <StyledTab
            label='Insights Explorer'
            {...a11yProps(1)}
            disabled={platform === 'DV360'}
          />
          <StyledTab label='How it Works' {...a11yProps(2)} />
          <StyledTab label='Planning' {...a11yProps(3)} />
        </Tabs>
      </Box>

      <TabPanel value={starvationTabIndex} index={0}>
        <Diagnostics />
      </TabPanel>
      <TabPanel value={starvationTabIndex} index={1}>
        <InsightsExplorer />
      </TabPanel>
      <TabPanel value={starvationTabIndex} index={2}>
        <HowItWorks />
      </TabPanel>
      <TabPanel value={starvationTabIndex} index={3}>
        <Planning />
      </TabPanel>
    </>
  );
};

export default StarvationBidderTabs;
